import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, MapPin, Building2, Globe, Phone, Mail, Package, Calendar, Star, Wifi, Home, Umbrella, Snowflake, QrCode, Loader2, StickyNote } from 'lucide-react';
import { API_URL } from '@/lib/api';
import { useVisitor } from '@/hooks/useVisitor';

export default function BoothDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const boothId = Number(id);
  const [booth, setBooth] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const { isBookmarked, toggleBookmark, logVisit, notes, setNote } = useVisitor();
  const [noteDraft, setNoteDraft] = useState('');
  const [showNoteEditor, setShowNoteEditor] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [boothRes, qrRes] = await Promise.all([
          axios.get(`${API_URL}/booths/${id}`),
          axios.get(`${API_URL}/booths/${id}/qr`),
        ]);
        setBooth(boothRes.data.data);
        setQrData(qrRes.data.data);
        // Auto-log visit for the visitor timeline
        if (boothRes.data?.data?.id) logVisit(Number(boothRes.data.data.id));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    // logVisit intentionally excluded from deps (stable ref via localStorage hook)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setNoteDraft(notes[boothId] || '');
  }, [boothId, notes]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate submission (backend endpoint can be added later)
    setSent(true);
    setTimeout(() => { setSent(false); setContactForm({ name: '', email: '', message: '' }); }, 3000);
  };

  // Sanitize QR SVG before rendering (XSS defense: strips <script>, event handlers, etc.)
  const sanitizedQrSvg = useMemo(() => {
    if (!qrData?.qr_svg) return '';
    return DOMPurify.sanitize(qrData.qr_svg, {
      USE_PROFILES: { svg: true, svgFilters: true },
    });
  }, [qrData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950">
        <Loader2 className="w-12 h-12 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!booth) return <div className="min-h-screen flex items-center justify-center text-white">Booth not found</div>;

  const company = booth.company;
  const amenities = [
    { key: 'has_shelter', icon: Umbrella, label: 'Shelter', color: 'text-blue-400' },
    { key: 'has_ac', icon: Snowflake, label: 'AC', color: 'text-cyan-400' },
    { key: 'has_wifi', icon: Wifi, label: 'WiFi', color: 'text-purple-400' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-emerald-500/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Booth Details</div>
            <div className="text-white text-lg font-black">{booth.booth_number}</div>
          </div>
          <Link to="/" className="text-slate-300 hover:text-white text-sm">← Back to Map</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hero */}
          <div 
            className="rounded-3xl p-8 shadow-2xl relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${booth.color || '#10b981'} 0%, ${booth.color || '#10b981'}88 100%)` }}
          >
            <div className="flex items-center gap-2 mb-3">
              {booth.is_premium && <span className="px-2.5 py-1 rounded-full bg-yellow-500 text-black text-xs font-black">PREMIUM</span>}
              {booth.is_corner && <span className="px-2.5 py-1 rounded-full bg-purple-500 text-white text-xs font-black">CORNER</span>}
              <span className="px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-bold">{booth.status?.toUpperCase()}</span>
            </div>
            <h1 className="text-4xl font-black text-white mb-2" data-testid="booth-title">
              {company?.name || 'Available Booth'}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{booth.hall?.name}</span>
              {booth.category && <span className="flex items-center gap-1.5">{booth.category.icon} {booth.category.name}</span>}
              {company?.country && <span>🌏 {company.country}</span>}
            </div>
            {/* Visitor actions: Bookmark + Add Note */}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => toggleBookmark(boothId)}
                className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 border transition-colors ${
                  isBookmarked(boothId)
                    ? 'bg-yellow-400 text-slate-900 border-yellow-300'
                    : 'bg-slate-900/60 text-white border-slate-700 hover:border-yellow-400 hover:text-yellow-300'
                }`}
                data-testid="bookmark-toggle-btn"
              >
                <Star className={`w-4 h-4 ${isBookmarked(boothId) ? 'fill-slate-900' : ''}`} />
                {isBookmarked(boothId) ? 'Bookmarked' : 'Bookmark'}
              </button>
              <button
                onClick={() => setShowNoteEditor((s) => !s)}
                className="px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 border bg-slate-900/60 text-white border-slate-700 hover:border-emerald-400 hover:text-emerald-300"
                data-testid="note-toggle-btn"
              >
                <StickyNote className="w-4 h-4" />
                {notes[boothId] ? 'Edit Note' : 'Add Note'}
              </button>
            </div>
          </div>

          {/* Note editor */}
          {showNoteEditor && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-emerald-500/40" data-testid="visitor-note-editor">
              <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-emerald-400" /> Your Private Note
              </h3>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onBlur={() => setNote(boothId, noteDraft)}
                placeholder="Reminders, contacts, follow-up ideas..."
                rows={4}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-slate-200 text-sm outline-none focus:border-emerald-500"
                data-testid="visitor-note-input"
              />
              <div className="text-slate-500 text-xs mt-1">Auto-saved locally on this device.</div>
            </div>
          )}

          {/* About */}
          {company?.description && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" /> About
              </h2>
              <p className="text-slate-300 leading-relaxed">{company.description}</p>
            </div>
          )}

          {/* Products / Gallery placeholder */}
          {company?.products?.length > 0 && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-400" /> Products & Services
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {company.products.map((p) => (
                  <div key={p.id} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-emerald-500/50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-white font-semibold">{p.name}</div>
                      {p.is_featured && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                    </div>
                    <div className="text-slate-400 text-sm">{p.description}</div>
                    {p.price && <div className="text-emerald-400 font-bold text-sm mt-2">RM {Number(p.price).toLocaleString()}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Events at this booth */}
          {booth.events?.length > 0 && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-400" /> Events at this Booth
              </h2>
              <div className="space-y-2">
                {booth.events.map((ev) => (
                  <div key={ev.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                    <div className="text-white font-semibold text-sm">{ev.title}</div>
                    <div className="text-slate-400 text-xs mt-1">
                      {new Date(ev.start_time).toLocaleString()} • {ev.type}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Form */}
          {company && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <h2 className="text-xl font-bold text-white mb-4">Contact Exhibitor</h2>
              {sent ? (
                <div className="p-4 rounded-lg bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 text-sm" data-testid="contact-success">
                  ✓ Message sent! The exhibitor will contact you soon.
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your Name"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="contact-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="contact-email-input"
                  />
                  <textarea
                    placeholder="Your Message"
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 resize-none"
                    data-testid="contact-message-input"
                  />
                  <button 
                    type="submit"
                    className="w-full py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold transition-all shadow-lg shadow-emerald-500/30"
                    data-testid="contact-submit-btn"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-6">
          {/* Amenities */}
          <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50">
            <h3 className="text-white font-bold mb-3">Booth Amenities</h3>
            <div className="space-y-2">
              {amenities.map(({ key, icon: Icon, label, color }) => (
                <div key={key} className={`flex items-center gap-3 p-2 rounded-lg ${booth[key] ? 'bg-slate-800/70' : 'bg-slate-800/20 opacity-50'}`}>
                  <Icon className={`w-5 h-5 ${booth[key] ? color : 'text-slate-600'}`} />
                  <span className={`text-sm ${booth[key] ? 'text-white' : 'text-slate-500'}`}>{label}</span>
                  <span className="ml-auto text-xs">{booth[key] ? '✓' : '—'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          {company && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50">
              <h3 className="text-white font-bold mb-3">Contact Info</h3>
              <div className="space-y-2 text-sm">
                {company.email && (
                  <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-slate-300 hover:text-white p-2 rounded hover:bg-slate-800/50">
                    <Mail className="w-4 h-4 text-blue-400" /> <span className="truncate">{company.email}</span>
                  </a>
                )}
                {company.phone && (
                  <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-slate-300 hover:text-white p-2 rounded hover:bg-slate-800/50">
                    <Phone className="w-4 h-4 text-emerald-400" /> {company.phone}
                  </a>
                )}
                {company.website && (
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-white p-2 rounded hover:bg-slate-800/50">
                    <Globe className="w-4 h-4 text-purple-400" /> <span className="truncate">{company.website}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* QR Code */}
          {qrData && (
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <QrCode className="w-4 h-4 text-emerald-400" /> Scan to Share
              </h3>
              <div 
                className="bg-white rounded-xl p-3 flex items-center justify-center"
                data-testid="booth-qr-code"
                dangerouslySetInnerHTML={{ __html: sanitizedQrSvg }}
              />
              <p className="text-slate-400 text-xs mt-3 text-center">Share this booth link</p>
            </div>
          )}

          {/* Stats */}
          <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50">
            <h3 className="text-white font-bold mb-3">Booth Info</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>Size</span><span className="text-white">{booth.width}×{booth.depth}m</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Zone</span><span className="text-white">{booth.hall?.name}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Views</span><span className="text-white">{booth.view_count}</span>
              </div>
              {booth.price && (
                <div className="flex justify-between text-slate-400">
                  <span>Rate</span><span className="text-emerald-400 font-bold">RM {Number(booth.price).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
